import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiCourseRequest } from "data/queries/ApiCourse";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Filters, FILTERS_QUERY } from "../query/getFilters";

export type NestedKey = { [nestedKey: string]: boolean };
export type FiltersState = { [key: string]: NestedKey };

export default function useFilters() {
  const [filters, setFilters] = useState<Filters>();
  const [stateFilters, setStateFilters] = useState<FiltersState>();
  const [order, setOrder] = useState("");
  const searchParams = useSearchParams();
  const router = useNavigate();
  const loadFilters = useCallback((categorySlug: any = null): any => {
    ApiCourseRequest<{ filterOptionsCourses: Filters }>({
      query: FILTERS_QUERY,
      variables: { categorySlug: categorySlug },
    })
      .then(({ data }) => {
        setFilters(data.data?.filterOptionsCourses);
      })
      .catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderBy = params.get("orderBy");
    setOrder(orderBy || "");
  }, [searchParams]);

  const getInitialState = useCallback((): FiltersState => {
    return {
      modalitiesNames:
        filters?.modalities.reduce((obj: NestedKey, item) => {
          obj[item.name] = false;
          return obj;
        }, {}) ?? {},
      educationalUnitNames:
        filters?.educationalUnits.reduce((obj: NestedKey, item) => {
          obj[item.visibleName] = false;
          return obj;
        }, {}) ?? {},
      teachingAreaNames:
        filters?.teachingAreas.reduce((obj: NestedKey, item) => {
          obj[item.name] = false;
          return obj;
        }, {}) ?? {},
      partnerVisibleNames:
        filters?.partners.reduce((obj: NestedKey, item) => {
          obj[item.visibleName] = false;
          return obj;
        }, {}) ?? {},
      states:
        filters?.states.reduce((obj: NestedKey, item) => {
          obj[item] = false;
          return obj;
        }, {}) ?? {},
      categoriesSlugs:
        filters?.subcategories.reduce((obj: NestedKey, item) => {
          obj[item.slug] = false;
          return obj;
        }, {}) ?? {},
    };
  }, [
    filters?.educationalUnits,
    filters?.modalities,
    filters?.partners,
    filters?.states,
    filters?.subcategories,
    filters?.teachingAreas,
  ]);

  useEffect(() => {
    const newState = getInitialState();
    const params = new URLSearchParams(window.location.search);

    for (const [key, value] of Object.entries(newState)) {
      if (params.has(key)) {
        const queryValues = params.getAll(key) ?? [];
        newState[key] = Object.fromEntries(
          Object.keys(value).map((item) => [item, queryValues.includes(item)])
        );
      } else {
        newState[key] = Object.fromEntries(Object.keys(value).map((item) => [item, false]));
      }
    }

    setStateFilters(newState);
  }, [getInitialState, router, searchParams]);

  const handleOrder = (event: any) => {
    const value = event.target.value;
    setOrder(value);

    const params = new URLSearchParams(window.location.search);
    params.set("orderBy", value);

    window.history.pushState({}, "", `${window.location.pathname}?${params.toString()}`);
  };

  const handleCheckbox = (evt: any, param: any) => {
    const checked = evt.target.checked;
    const value = evt.target.value;

    const params = new URLSearchParams(window.location.search);

    if (checked) {
      params.append(param, value);
    } else {
      const allValues = params.getAll(param);
      params.delete(param); // Remove todas as ocorrências primeiro
      allValues
        .filter((item) => item !== value) // Reinsere os valores restantes
        .forEach((item) => params.append(param, item));
    }

    window.history.pushState({}, "", `${window.location.pathname}?${params.toString()}`);
  };

  return {
    filters,
    loadFilters,
    stateFilters,
    handleOrder,
    handleCheckbox,
    order,
  };
}
